import { useState, useEffect } from 'react'
import { X, Plus, Trash, FloppyDisk } from '@phosphor-icons/react'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import MoneyInput from '../MoneyInput'
import { SavedTemplate } from '../../hooks/useSavedTemplates'
import { BreakdownItem } from './FeeBreakdown'
import {
  FeeCategory,
  CATEGORY_LABELS,
  getFeeTemplatesByModality,
  getFeesByCategory,
} from '../../constants/feeTemplates'

interface EditTemplateModalProps {
  template: SavedTemplate
  isOpen: boolean
  onClose: () => void
  onSave: (id: string, name: string, breakdown: BreakdownItem[]) => void
}

export function EditTemplateModal({
  template,
  isOpen,
  onClose,
  onSave,
}: EditTemplateModalProps) {
  const [name, setName] = useState(template.name)
  const [breakdown, setBreakdown] = useState<BreakdownItem[]>([])
  const [customFeeName, setCustomFeeName] = useState('')
  const [customFeeCategory, setCustomFeeCategory] = useState<FeeCategory>('additional')

  // Load template data when opened
  useEffect(() => {
    if (isOpen && template) {
      setName(template.name)
      // Add currency back to breakdown items
      setBreakdown(
        template.breakdown.map(item => ({
          ...item,
          currency: 'USD' as const,
        }))
      )
    }
  }, [isOpen, template])

  if (!isOpen) return null

  const availableFees = getFeeTemplatesByModality(template.modality)

  // Check if a fee is in the breakdown
  const isFeeSelected = (feeId: string): boolean => {
    return breakdown.some(item => item.id === feeId)
  }

  // Get fee value
  const getFeeValue = (feeId: string): number => {
    const item = breakdown.find(b => b.id === feeId)
    return item?.value || 0
  }

  // Toggle fee selection
  const toggleFee = (fee: { id: string; name: string; category: FeeCategory }) => {
    if (isFeeSelected(fee.id)) {
      setBreakdown(breakdown.filter(item => item.id !== fee.id))
    } else {
      setBreakdown([
        ...breakdown,
        {
          id: fee.id,
          name: fee.name,
          category: fee.category,
          value: 0,
          currency: 'USD',
          isCustom: false,
        },
      ])
    }
  }

  // Update fee value
  const updateFeeValue = (feeId: string, value: number) => {
    setBreakdown(
      breakdown.map(item =>
        item.id === feeId ? { ...item, value: Math.max(0, value) } : item
      )
    )
  }

  // Add custom fee
  const addCustomFee = () => {
    if (!customFeeName.trim()) return

    const id = `custom-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    setBreakdown([
      ...breakdown,
      {
        id,
        name: customFeeName.trim(),
        category: customFeeCategory,
        value: 0,
        currency: 'USD',
        isCustom: true,
      },
    ])
    setCustomFeeName('')
  }

  // Remove custom fee
  const removeCustomFee = (id: string) => {
    setBreakdown(breakdown.filter(item => item.id !== id))
  }

  // Handle save
  const handleSave = () => {
    if (!name.trim()) return
    // Only save items with values > 0
    const validBreakdown = breakdown.filter(item => item.value > 0)
    onSave(template.id, name.trim(), validBreakdown)
    onClose()
  }

  // Render fees for a category
  const renderCategoryFees = (category: FeeCategory) => {
    const categoryFees = getFeesByCategory(availableFees, category)
    const customFees = breakdown.filter(item => item.isCustom && item.category === category)

    if (categoryFees.length === 0 && customFees.length === 0) return null

    return (
      <div key={category} className="mb-4">
        <h4 className="text-sm font-semibold text-gray-700 mb-2">
          {CATEGORY_LABELS[category]}
        </h4>
        <div className="space-y-2">
          {categoryFees.map(fee => {
            const isSelected = isFeeSelected(fee.id)
            const value = getFeeValue(fee.id)

            return (
              <div
                key={fee.id}
                className={`flex items-center gap-3 p-2 rounded-lg border transition-colors ${
                  isSelected
                    ? 'bg-emerald-50 border-emerald-200'
                    : 'bg-gray-50 border-gray-200'
                }`}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => toggleFee(fee)}
                  className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                />
                <span className="flex-1 text-sm text-gray-900">{fee.name}</span>
                {isSelected && (
                  <MoneyInput
                    value={value || undefined}
                    onChange={(val) => updateFeeValue(fee.id, val)}
                    currency="USD"
                    size="sm"
                    className="w-32"
                  />
                )}
              </div>
            )
          })}

          {/* Custom fees for this category */}
          {customFees.map(fee => (
            <div
              key={fee.id}
              className="flex items-center gap-3 p-2 rounded-lg border bg-blue-50 border-blue-200"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <div className="w-2 h-2 rounded-full bg-blue-600" />
              </div>
              <span className="flex-1 text-sm text-gray-900">{fee.name}</span>
              <MoneyInput
                value={fee.value || undefined}
                onChange={(val) => updateFeeValue(fee.id, val)}
                currency="USD"
                size="sm"
                className="w-32"
              />
              <button
                onClick={() => removeCustomFee(fee.id)}
                className="p-1 text-red-500 hover:text-red-700 hover:bg-red-100 rounded"
              >
                <Trash size={14} />
              </button>
            </div>
          ))}
        </div>
      </div>
    )
  }

  const categories: FeeCategory[] = ['freight', 'origin', 'destination', 'additional']
  const hasValidItems = breakdown.some(item => item.value > 0)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Editar Template</h2>
            <p className="text-sm text-gray-500">
              Modalidade: {template.modality === 'maritimo' ? 'Marítimo' :
                          template.modality === 'aereo' ? 'Aéreo' : 'Terrestre'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* Template Name */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nome do Template
            </label>
            <Input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: Template Padrão Marítimo"
              className="w-full"
            />
          </div>

          {/* Fees by Category */}
          <div className="mb-6">
            <h3 className="text-base font-semibold text-gray-900 mb-3">
              Taxas do Template
            </h3>
            {categories.map(category => renderCategoryFees(category))}
          </div>

          {/* Add Custom Fee */}
          <div className="border-t border-gray-200 pt-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">
              Adicionar Taxa Personalizada
            </h4>
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <Input
                  type="text"
                  placeholder="Nome da taxa..."
                  value={customFeeName}
                  onChange={(e) => setCustomFeeName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      addCustomFee()
                    }
                  }}
                />
              </div>
              <select
                value={customFeeCategory}
                onChange={(e) => setCustomFeeCategory(e.target.value as FeeCategory)}
                className="h-10 px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value="origin">Origem</option>
                <option value="freight">Frete</option>
                <option value="destination">Destino</option>
                <option value="additional">Adicional</option>
              </select>
              <Button
                type="button"
                onClick={addCustomFee}
                disabled={!customFeeName.trim()}
                size="default"
                variant="outline"
              >
                <Plus size={16} />
              </Button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-gray-50">
          <p className="text-sm text-gray-500">
            {breakdown.filter(b => b.value > 0).length} taxas com valor definido
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={onClose}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              disabled={!name.trim() || !hasValidItems}
              style={{ backgroundColor: 'rgb(5, 150, 105)' }}
              className="gap-2"
            >
              <FloppyDisk size={16} />
              Salvar Alterações
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
